/**
 * ProviderKey domain types (Routing v2).
 *
 * Un credential nommé PUR vers un provider upstream (anthropic, openai,
 * anthropic-bedrock). Plus de mapping tier→model embarqué — le routing
 * vit désormais sur l'ApiKey.
 *
 * Cf. backend `internal/domain/providerkey` + handler control-plane
 * `/api/v1/provider-keys/*`.
 */

/**
 * Types de provider supportés (liste statique alignée avec le backend).
 * V1.5 différé : azure-openai, vertex.
 */
export const PROVIDER_TYPES = ['anthropic', 'openai', 'anthropic-bedrock'] as const
export type ProviderType = (typeof PROVIDER_TYPES)[number]

export interface ProviderKey {
    readonly id: string
    readonly tenantId: string
    readonly name: string
    readonly providerType: ProviderType
    /** Endpoint custom optionnel (backends OpenAI-compatible). Vide = URL par défaut du provider. */
    readonly baseUrl: string
    /**
     * Zone du mix électrique (ISO-3, ex « FRA », « USA ») du datacenter de ce
     * credential, pour le GreenOps. Vide = inconnue (zone par défaut). Deux clés
     * de même provider_type (ex openai) peuvent différer : OpenAI/USA vs
     * Scaleway/FRA — le GWP varie ~×5.
     */
    readonly electricityZone: string
    readonly disabled: boolean
    readonly createdAt: string
    readonly updatedAt: string
}

/**
 * Input POST /api/v1/provider-keys.
 * `credentialSecretBase64` = base64 stdEncoding du secret brut (sk-ant-…,
 * AWS JSON, …). Stocké côté SecretStore avec la convention `provider_key:<id>`.
 */
export interface CreateProviderKeyInput {
    readonly tenantId: string
    readonly name: string
    readonly providerType: ProviderType
    /** Endpoint custom optionnel (OpenAI-compatible). Omis/vide = URL par défaut. */
    readonly baseUrl?: string
    /** Zone du mix électrique (ISO-3) du datacenter. Omis/vide = inconnue. */
    readonly electricityZone?: string
    readonly credentialSecretBase64: string
}

/**
 * Input PATCH /api/v1/provider-keys/{id}. Partial update.
 */
export interface UpdateProviderKeyInput {
    readonly name?: string
    readonly baseUrl?: string
    readonly electricityZone?: string
    readonly disabled?: boolean
}

/**
 * ModelInfo — un modèle disponible chez un provider (GET .../models).
 */
export interface ModelInfo {
    readonly id: string
    readonly displayName: string
}

/**
 * Résultat du fetch des modèles : `supported=false` si le provider ne
 * supporte pas le listing (Bedrock) ou si l'API est injoignable (air-gap)
 * → l'UI retombe sur la saisie libre.
 */
export interface ProviderKeyModels {
    readonly supported: boolean
    readonly models: readonly ModelInfo[]
    readonly error?: string
}

export type ProviderKeyErrorCode =
    | 'not_found'
    | 'duplicate_name'
    | 'invalid_input'
    | 'secret_required'
    | 'invalid_secret'
    | 'tenant_scope'
    | 'secret_store_unavailable'
    | 'csrf_required'
    | 'csrf_invalid'
    | 'unauthorized'
    | 'forbidden'
    | 'internal_error'
    | 'network_error'
    | 'unknown'

export class ProviderKeyError extends Error {
    readonly code: ProviderKeyErrorCode
    readonly httpStatus: number | null
    constructor(code: ProviderKeyErrorCode, message: string, httpStatus: number | null = null) {
        super(message)
        this.code = code
        this.httpStatus = httpStatus
        this.name = 'ProviderKeyError'
    }
}

export function isProviderKeyError(err: unknown): err is ProviderKeyError {
    return err instanceof ProviderKeyError
}
